import type {
  ExportFile,
  ExportResult,
  FileExporter,
  OpenOptions,
  OpenResult,
} from '@anytracker/core'

/**
 * The web implementation of core's `FileExporter` port.
 *
 * A Blob and an anchor with a `download` attribute on the way out; a file input
 * on the way back in. Both are browser APIs, which is exactly why core declares
 * the interface and this supplies it (ADR-0002) — the native app will use a
 * share sheet and a document picker and no screen will change.
 *
 * Nothing here touches the network. The Blob is built from bytes already in
 * memory and handed to the browser's own download machinery, so an export works
 * with the device in aeroplane mode — which is the point, and is asserted by
 * the offline end-to-end test.
 */
export class BlobExporter implements FileExporter {
  save(file: ExportFile): Promise<ExportResult> {
    try {
      const blob = new Blob([file.content], { type: file.mimeType })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = file.filename
      anchor.rel = 'noopener'
      anchor.style.display = 'none'

      document.body.append(anchor)
      anchor.click()
      anchor.remove()

      /*
       * Revoked on a later task, not immediately.
       *
       * Revoking synchronously after `click()` races the browser: the download
       * has been requested but not necessarily started, and Safari in
       * particular cancels it outright when the object URL disappears first.
       * The symptom is a download that silently never happens, on one browser,
       * intermittently — so the delay stays.
       */
      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 0)

      return Promise.resolve({ status: 'saved', filename: file.filename })
    } catch (error) {
      return Promise.resolve({
        status: 'failed',
        reason: error instanceof Error ? error.message : 'The download could not start.',
      })
    }
  }

  /**
   * Asks for a file with a hidden `<input type="file">`.
   *
   * The `cancel` event is what resolves a dismissed picker. Where a browser does
   * not fire it the promise simply never settles, and that is deliberately not
   * papered over with a focus-based guess: the callers here do not block on
   * this promise, so an unresolved one costs nothing, while a focus heuristic
   * fires spuriously and reports a cancellation over a file the user did pick.
   */
  open(options?: OpenOptions): Promise<OpenResult> {
    return new Promise<OpenResult>((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      if (options?.extensions !== undefined && options.extensions.length > 0) {
        input.accept = options.extensions.join(',')
      }
      input.style.display = 'none'

      const settle = (result: OpenResult) => {
        input.remove()
        resolve(result)
      }

      input.addEventListener('cancel', () => {
        settle({ status: 'cancelled' })
      })

      input.addEventListener('change', () => {
        const file = input.files?.[0]
        if (file === undefined) {
          settle({ status: 'cancelled' })
          return
        }
        file.text().then(
          (text) => {
            settle({ status: 'opened', file: { filename: file.name, text } })
          },
          (error: unknown) => {
            settle({
              status: 'failed',
              reason:
                error instanceof Error ? error.message : 'The file could not be read.',
            })
          },
        )
      })

      document.body.append(input)
      input.click()
    })
  }
}
